import React, { useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { Card, CardContent, Container } from '@mui/material';

import OfferBreadcrumbs from '~/components/Common/Elements/OfferBreadcrumbs';
import PageHeader from '~/components/Common/Elements/PageHeader';

import OffersService from '../../../services/OffersService';
import ReservationService from '../../../services/ReservationService';
import mergeRoomPolicyNames from '../../../utils/mergeRoomPolicyNames';
import RoomPolicyForm from '../../Common/Forms/RoomPolicyForm';

import OfferTemplateModal from './OfferTemplateModal';

export default function RoomPoliciesEditContainer() {
  const { id_offer: idOffer } = useParams<{ id_offer: string }>();

  const [roomPoliciesWithNames, setRoomPoliciesWithNames] = useState([]);
  const [roomPoliciesSchema, setRoomPoliciesSchema] = useState({});
  const [offer, setOffer] = useState(null);

  const [currTemplateType, setCurrTemplateType] = useState('');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [offerTemplates, setOfferTemplates] = useState([]);
  const [dealId, setDealId] = useState('');
  const [selectedRoomPolicySalesforceId, setSelectedRoomPolicySalesforceId] = useState('');

  const [roomRateId, setRoomRateId] = useState('');
  const [roomTypeId, setRoomTypeId] = useState('');
  const [propertyId, setPropertyId] = useState('');

  useEffect(() => {
    OffersService.getOffer(idOffer).then((response) => {
      setOffer(response.result);
    });
  }, [idOffer]);

  useEffect(() => {
    if (offer) {
      Promise.all([
        OffersService.getRoomPolicySchema(idOffer),
        ReservationService.getProperties(offer.vendor_account_id),
        OffersService.getOfferTemplates(),
      ]).then(([schemaResponse, propertyResponse, offerTemplatesResponse]) => {
        const propertyData = propertyResponse.count ? propertyResponse.result : [];

        setRoomPoliciesSchema(schemaResponse.schema);
        setRoomPoliciesWithNames(mergeRoomPolicyNames(offer.packages, propertyData));
        setOfferTemplates(offerTemplatesResponse.filter((template) => template.status === 'approved'));
        setRoomRateId(offer.packages[0]?.fk_room_rate_id);
        setRoomTypeId(offer.packages[0]?.fk_room_type_id);
        setPropertyId(offer.packages[0]?.fk_property_id);
      });
    }
  }, [idOffer, offer]);

  const onShowTemplateModalClick = (templateType: string, salesforceId: string) => {
    setCurrTemplateType(templateType);
    setDealId(salesforceId);
    setSelectedRoomPolicySalesforceId(salesforceId);
    setShowTemplateModal(true);
  };

  const filteredTemplates = useMemo(
    () =>
      offerTemplates.filter((template) => {
        const templateOfferType = template.offer_type === 'all' ? true : offer.type === template.offer_type;
        return template.template_type === currTemplateType && templateOfferType;
      }),
    [currTemplateType, offer, offerTemplates],
  );

  const loadTemplateToEditor = (template: App.OfferTemplate) => {
    setRoomPoliciesWithNames((prev) => {
      const newRoomPolicies = prev.map((roomPolicy) => {
        if (roomPolicy.salesforce_id === selectedRoomPolicySalesforceId) {
          return {
            ...roomPolicy,
            description: template.content,
          };
        }
        return roomPolicy;
      });
      return newRoomPolicies;
    });
  };

  return (
    <>
      <OfferTemplateModal
        show={showTemplateModal}
        hide={() => setShowTemplateModal(false)}
        templates={filteredTemplates}
        templateType={currTemplateType}
        loadTemplate={loadTemplateToEditor}
        propertyId={propertyId}
        roomTypeId={roomTypeId}
        roomRateId={roomRateId}
        dealId={dealId}
      />
      <Container maxWidth="xl">
        <OfferBreadcrumbs offerId={idOffer} currentPage="Room Policies" />
        <PageHeader title="Edit Room Policies" />
        {roomPoliciesWithNames.map((roomPolicy, index) => (
          <Card raised sx={{ m: 2 }} key={index}>
            <CardContent>
              <RoomPolicyForm
                key={`${roomPolicy.id}-${roomPolicy.description}`}
                schema={roomPoliciesSchema}
                roomPolicy={roomPolicy}
                idOffer={idOffer}
                onShowTemplateModalClick={onShowTemplateModalClick}
              />
            </CardContent>
          </Card>
        ))}
      </Container>
    </>
  );
}
